import type { UserManagementApi } from '@liks/backend-svc-api/esm/apis/UserManagementApi';
import type { UserGroup } from '@liks/backend-svc-api/esm/models/index';
import type { WithResourceId } from '../types/knowledge';
import {
  hasNamedResourceId,
  toAssignedDirectoryUser,
} from './userServiceGuards';
import type { AssignedDirectoryUser } from './userServiceTypes';

export async function listGroups(
  userApi: UserManagementApi,
): Promise<Array<WithResourceId<UserGroup> & { name: string }>> {
  const groups =
    await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceListGroups();
  return groups.filter(hasNamedResourceId);
}

export async function createGroup(
  userApi: UserManagementApi,
  name: string,
): Promise<UserGroup> {
  return userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceCreateGroup({
    userGroup: { name },
  });
}

export async function renameGroup(
  userApi: UserManagementApi,
  groupId: string,
  name: string,
): Promise<UserGroup> {
  return userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceRenameGroup({
    groupId,
    userGroupRenameRequest: { name },
  });
}

export async function deleteGroup(
  userApi: UserManagementApi,
  groupId: string,
): Promise<void> {
  await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceDeleteGroup({
    groupId,
  });
}

export async function listUserGroups(
  userApi: UserManagementApi,
  userId: string,
): Promise<Array<WithResourceId<UserGroup> & { name: string }>> {
  const groups =
    await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceListUserGroups(
      { userId },
    );
  return groups.filter(hasNamedResourceId);
}

export async function listGroupMembers(
  userApi: UserManagementApi,
  groupId: string,
): Promise<AssignedDirectoryUser[]> {
  const users =
    await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceListGroupMembers(
      { groupId },
    );
  return users
    .map(toAssignedDirectoryUser)
    .filter((user): user is AssignedDirectoryUser => user != null);
}

export async function assignUserGroup(
  userApi: UserManagementApi,
  userId: string,
  groupId: string,
): Promise<void> {
  await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceAssignUserGroup(
    { userId, groupId },
  );
}

export async function unassignUserGroup(
  userApi: UserManagementApi,
  userId: string,
  groupId: string,
): Promise<void> {
  await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceUnassignUserGroup(
    { userId, groupId },
  );
}
